<template>
    <div id="future-prescriptions-queue-container">
        <card title="Pending For Billing Queue">
            <div>
                <b-row>
                    <b-col>
                        <SearchComponent v-model="storeFilter" label="Store" search-u-r-l="/Store" :params="{active: true}" prepopulate clearable
                            @input="search" />
                    </b-col>
                    <b-col>
                        <SearchComponent v-model="programFilter" label="Program" search-u-r-l="/Program" prepopulate
                            clearable @input="search" />
                    </b-col>
                </b-row>
                <b-button variant="success" @click="processQueue"
                    :disabled="!(prescriptions && prescriptions.length) || disableProcessButton">
                    Process Queue
                </b-button>
            </div>

            <PrescriptionsListComponent ref="prescriptionList" :prescriptions="pagedPrescriptions"
                :showGeneralActionButtons="false" :showSelectCheckox="false" batchModalTitle="Pending for Billing"
                :actionButtons="['edit']" :showVoidBox="false"
                :manual-sort="['fillDate', 'productNameShort', 'programName']" :default-sort="sortBy"
                @sort-changed="handleSort" @batchFinished="onBatchFinished" />

            <BottomPagerBar v-model="pageNumber" :total-rows="prescriptions.length" :per-page="pageSize" show-total
                item-name="prescription" :show-page-size-selector="true" :loading="loading"
                @change-page-size="pageSize = $event" />
        </card>
    </div>
</template>

<script lang="ts">
import { Component, Watch, Vue } from 'vue-property-decorator';
import Axios from 'axios';
import { DatePicker } from "element-ui";
import { Prescription } from '@/models/Prescription';
import { Store } from '@/models/Store';
import SearchComponent from '@/components/SearchComponent.vue';
import EnumSelector from '@/components/EnumSelector.vue';
import PrescriptionsListComponent from '@/components/Prescription/PrescriptionsListComponent.vue';
import { Program } from "@/models/Program";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import BottomPagerBar from '@/components/BottomPagerBar.vue';

@Component({
    name: "PendingForBillingQueuePage",
    components: {
        SearchComponent,
        EnumSelector,
        [DatePicker.name]: DatePicker,
        PrescriptionsListComponent,
        BottomPagerBar
    },
})
export default class PendingForBillingQueuePage extends Vue {
    private prescriptions: Array<Prescription> = [];
    private queueForProcessing: Array<Prescription> = [];
    private storeFilter: Store = new Store();
    private programFilter: Program = new Program();

    protected pageNumber: number = 1;
    protected pageSize: number = 100;
    protected loading: boolean = true;

    private sortBy: string = "fillDate";
    private sortDirection: string = "D";

    @Watch("storeFilter")
    @Watch("programFilter")
    resetPage() {
        this.pageNumber = 1;
    }

    created() {
        this.search();
    }

    async search() {
        try {
            this.loading = true;
            const response = await Axios.get(`/Prescription/get-pending-billing`, {
                params: {
                    storeId: this.storeFilter?.id,
                    programId: this.programFilter?.id
                }
            });

            this.prescriptions = response.data;
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
            console.warn("Error while fetching pending billing", error);
        } finally {
            this.loading = false;
        }
    }

    processQueue() {
        // Create a snapshot of sorted prescriptions for batch processing
        // This prevents the batch from being affected by parent component refreshes
        this.queueForProcessing = [...this.sortedPrescriptions];
        (this.$refs.prescriptionList as any).openBatch(this.queueForProcessing);
    }

    onBatchFinished() {
        // Clear the batch queue snapshot
        this.queueForProcessing = [];
        // Refresh the prescription list after entire batch is complete
        this.search();
    }

    get disableProcessButton(): boolean {
        return !this.prescriptions.length;
    }

    get sortedPrescriptions() {
        let sorted = [...this.prescriptions];

        switch (this.sortBy) {
            case "productNameShort":
                sorted.sort((a, b) => a.productNameShort?.localeCompare(b.productNameShort));
                break;
            case "programName":
                sorted.sort((a, b) => a.programName?.trim().localeCompare(b.programName?.trim()));
                break;
            default:
                sorted.sort((a, b) => new Date(a.fillDate).getTime() - new Date(b.fillDate).getTime());
                break;
        }

        if (this.sortDirection === "D") sorted.reverse();

        return sorted;
    }

    get pagedPrescriptions() {
        const startIndex = this.pageSize * this.pageNumber - this.pageSize;
        const endIndex = startIndex + this.pageSize;
        return this.sortedPrescriptions.slice(startIndex, endIndex);
    }

    handleSort(event: any) {
        this.sortBy = event.sortBy;
        this.sortDirection = event.sortDesc ? "D" : "A";
        this.pageNumber = 1;
    }
}

</script>
